import Cookie from './cookie.js';

/**
 * @typedef {object} optOutCategories
 * @prop {{[categoryId: string]: string}} Implements activeCatIdMap Active categories API from Tealium
 */

/**
 * Regular expression that determines whether a given
 * string is a value "cvalue" for OPTOUTMULTI cookie.
 * e.g. 0:0|c2:0|c9:0|c11:0
 *
 * @type {RegExp}
 */
const isCValue = /(^0:)|(^[Cc][0-9]{1,3})/;

export default class OptOutMulti extends Cookie {
  constructor({ optOutCats, domain, context }) {
    super({ name: 'OPTOUTMULTI', domain });

    this.context = context;
    this.optOutCats = optOutCats;
    this.consents = Object.keys(optOutCats).reduce((acc, category) => {
      acc[category] = 1;
      return acc;
    }, {});
  }

  /**
   * Used to set OPTOUTMULTI cookie on the current base domain in
   * exactly the way that Tealium is expecting to read it
   *
   * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
   * Using `encodeUri` NON-STANDARD – see citation below that
   * TEALIUM DOES DO THIS inside of the Consent Manager Tag template
   * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
   *
   *     getCookieValues : function(){
   *         var values = {},
   *             rcd    = (function(){
   *                 var value = "; " + document.cookie;
   *                 var parts = value.split("; " + utag.gdpr.cookieNS + "=");
   *                 if (parts.length == 2) return utag.ut.decode(parts.pop().split(";").shift());
   *             }()),
   *             cd     = utag.gdpr.typeOf(rcd) === "string" ? rcd : "";
   *
   *         if (utag.data && cd) {
   *             utag.data["cp." + utag.gdpr.cookieNS] = cd;
   *         }
   *
   *         if (cd) {
   *             var i,
   *                 optOut,
   *                 optOutData = decodeURI(cd).split("|");
   *             for (i = 0; i < optOutData.length; i++){
   *                 optOut = optOutData[i].split(":");
   *                 values[optOut[0]] = optOut[1];
   *             }
   *         }
   *         utag.gdpr.values = values;
   *         return values;
   *     },
   *
   * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
   * If we do not do this then Tealium will be
   * UNABLE TO READ the values that we set
   * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
   *
   *     node -pe "decodeURI(encodeURIComponent('0:0|c2:0|c9:0|c11:0'))"
   *     0%3A0|c2%3A0|c9%3A0|c11%3A0'
   *
   * @param {Object} opts Options
   * @param {string} [opts.value]   The value of the cookie to set
   * @public
   */
  write({ value }) {
    this.value = value;

    let cookie = `${encodeURI(this.name)}=${encodeURI(value)}`;

    const attrs = this.attributes;
    if ('expires' in attrs) cookie += '; expires=' + attrs.expires;
    if ('path' in attrs) cookie += '; path=' + attrs.path;
    if ('domain' in attrs) cookie += '; domain=' + attrs.domain;

    //
    // Which is why we must break encapsulation to call
    // `document.cookie = cookie` within OptOutMulti
    //
    const doc = this.context.document || {};
    doc.cookie = cookie;
  }

  /**
   * Remove anything from the cookie that does not
   * match a valid category name
   *
   * @returns {boolean} Value indicating if repairs were attempted
   * @public
   */
  repair() {
    if (!this.value) this.read();

    const hasValue = !!this.value;
    if (!hasValue) return false;

    const optOutValues = this.value
      .split('|')
      .filter(cv => isCValue.test(cv));

    //
    // Delete original cookie before writing new.
    // This **MUST** be done due to Safari's limitations
    //
    this.remove('OPTOUTMULTI');
    this.write({ value: optOutValues.join('|') });
    return true;
  }

  /**
   * Serializes the target optOutCats into a valid cookie
   * string value with all categories default disabled
   *
   * @returns {string} Cookie string value
   */
  toStringValue() {
    const { optOutCats, consents } = this;
    return Object.keys(consents).reduce((acc, category) => {
      return `${acc}|c${optOutCats[category]}:${consents[category]}`;
    }, '0:0');
  }

  /**
   * Writes OPTOUTMULTI value for the opt-out categories associated
   * with this instance for a given action to {enable,disable} all tracking
   *
   * @param {"enable"|"disable"|string} action What state transition to take
   * @public
   */
  setOptOutAction(action) {
    this.setConsents(action);
    this.write({ value: this.toStringValue() });
  }

  /**
   * Updates the consents value for the target value. One of:
   *
   *   ['enable', 'disable', { <category>: <0 => enable, 1 => disable> }]
   *
   * @param  {string|object} target New target to set consents for this instance with
   */
  setConsents(target) {
    if (typeof target === 'string') {
      Object.keys(this.consents).forEach(category => {
        this.consents[category] = target === 'enable' ? 0 : 1;
      });
    } else if (typeof target === 'object') {
      this.consents = {
        ...this.consents,
        ...target
      };
    }
  }
}
